import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Button,
    FormHelperText,
    Grid,
    MenuItem,
    Divider,
    Typography,
    Stack,
    TextField as TextFieldMUI,
    FormGroup,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { styles } from '../../../features/users/UserDetailsTab/styles';
import TextField from '../../../components/common/InputField/TextField';
import { utilStyles } from '../../../utils/styles';
import { setLoading } from '../../../store/slices/app';
import { useSaveCompanyMutation, useModifyCompanyActionMutation } from '../../../api/company';

const CompanyTab = ({ isEditMode, company = {}, address }) => {
    const { customer } = company;
    const initialAddress = {
        addressLine1: isEditMode ? address?.addressLine1 : '',
        addressLine2: isEditMode ? address?.addressLine2 : '',
        addressLine3: isEditMode ? address?.addressLine3 : '',
        city: isEditMode ? address?.city : '',
        country: isEditMode ? address?.country : '',
        state: isEditMode ? address?.state : '',
        locationName: isEditMode ? address?.locationName : '',
        postalCode: isEditMode ? address?.pinOrZip : '',
    };
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [saveCompany, { isLoading: isSaveLoading }] = useSaveCompanyMutation();
    const [modifyCompanyAction, { isLoading: isModifyLoading }] = useModifyCompanyActionMutation();

    const isLoading = isSaveLoading || isModifyLoading;

    const handleSubmit = async (details) => {
        const bodyFormData = new FormData();
        const data = {
            'customer.customerStatus': details.status,
            'customer.numberOfEmployees': details.numberOfEmployees,
            'customer.description': details.description,
            companyName: details.companyName,
            'address.locationName': details.locationName,
            'address.addressLine1': details.addressLine1,
            'address.addressLine2': details.addressLine2,
            'address.addressLine3': details.addressLine3,
            'address.city': details.city,
            'address.state': details.state,
            'address.country': details.country,
            'address.pinOrZip': details.postalCode,
        };

        if (!isEditMode) {
            data['user.username'] = details.email;
            data['user.employee.emailAddresses[0]'] = details.email;
            data['user.employee.firstName'] = details.firstName;
            data['user.employee.lastName'] = details.lastName;
            data['user.employee.mobileNumbers[0]'] = details.contactNo;

            data['isAnsrHqEnabled'] = `${details.ansrHQ}`;
            data['isClientHqEnabled'] = `${details.clientHQ}`;

            data['companySlug'] = details.companySlug;
        } else {
            data['customer.id'] = customer?.id;
            data['id'] = company?.id;
            data['address.id'] = company?.hoAddressID;

            if (company?.isClientHqEnabled === false) data['isClientHqEnabled'] = `${details.clientHQ}`;
        }

        Object.keys(data).forEach((key) => {
            if (data[key]) {
                bodyFormData.append(key, data[key]);
            }
        });

        try {
            dispatch(setLoading(true));
            const apiPath = isEditMode ? modifyCompanyAction : saveCompany;
            const response = await apiPath(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message, { variant: 'success' });
            navigate('/company');
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        } finally {
            dispatch(setLoading(false));
        }
    };

    const statusTypeOptions = ['Active', 'Inactive'];
    const adminValidations = isEditMode
        ? {}
        : {
              email: Yup.string().email('Invalid email').required('Email is required'),
              firstName: Yup.string().required('First name is required'),
              lastName: Yup.string().required('Last name is required'),
              contactNo: Yup.number()
                  .typeError('Invalid Contact No.')
                  .positive('Invalid Contact No.')
                  .test('len', 'Invalid Contact No.', (value) => {
                      const length = value?.toString().length || 0;
                      return length === 0 || length === 10;
                  })
                  .optional(),
          };

    const adminInitialValues = isEditMode
        ? {}
        : {
              username: '',
              email: '',
              firstName: '',
              lastName: '',
              contactNo: '',
          };

    const formik = useFormik({
        validateOnChange: true,
        validateOnBlur: false,
        enableReinitialize: true,
        initialValues: {
            companyName: isEditMode ? customer?.customerName : '',
            numberOfEmployees: isEditMode ? customer?.numberOfEmployees : '',
            status: isEditMode ? customer?.customerStatus : 'Active',
            description: isEditMode ? customer?.description : '',
            ansrHQ: isEditMode ? company?.isAnsrHqEnabled : true,
            clientHQ: isEditMode ? company?.isClientHqEnabled : false,
            ...(!isEditMode && {
                companySlug: '',
            }),
            ...adminInitialValues,
            ...initialAddress,
        },
        validationSchema: Yup.object().shape({
            companyName: Yup.string().required('Company name is required'),
            ...(!isEditMode && {
                companySlug: Yup.string()
                    .required('Company slug is required')
                    .matches(/^[a-z0-9-]+$/, 'Company slug must contain only lowercase letters, numbers and hyphens'),
            }),
            ...adminValidations,
        }),
        onSubmit: async (values, helpers) => {
            try {
                handleSubmit(values);
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
            } catch (err) {
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({
                    submit: err.message,
                });
                helpers.setSubmitting(false);
            }
        },
    });

    return (
        <>
            <Grid container spacing={4}>
                <Grid item md={12} xs={12}>
                    <form onSubmit={formik.handleSubmit}>
                        <div>
                            <Grid container columnSpacing={6} rowSpacing={3}>
                                <Grid xs={12} item sx={styles.headerWrapper}>
                                    <Grid xs={8.27}>
                                        <Typography>Basic Information</Typography>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        error={Boolean(formik.touched.companyName && formik.errors.companyName)}
                                        helperText={formik.touched.companyName && formik.errors.companyName}
                                        fullWidth
                                        label="Company Name"
                                        name="companyName"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        value={formik.values.companyName}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        error={Boolean(
                                            formik.touched.numberOfEmployees && formik.errors.numberOfEmployees,
                                        )}
                                        fullWidth
                                        helperText={formik.touched.numberOfEmployees && formik.errors.numberOfEmployees}
                                        label="Number Of Employees"
                                        name="numberOfEmployees"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        value={formik.values.numberOfEmployees}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        error={Boolean(formik.touched.status && formik.errors.status)}
                                        fullWidth
                                        helperText={formik.touched.status && formik.errors.status}
                                        label="Status"
                                        name="status"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        select
                                        value={formik.values.status}
                                    >
                                        {statusTypeOptions?.map((relationshipTypeOption) => (
                                            <MenuItem key={relationshipTypeOption} value={relationshipTypeOption}>
                                                {relationshipTypeOption}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} sx={styles.checkboxWrapper}>
                                    <TextFieldMUI
                                        fullWidth
                                        multiline
                                        label="Description"
                                        size="small"
                                        variant="outlined"
                                        InputLabelProps={{
                                            shrink: formik.values.description,
                                        }}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        name="description"
                                        rows={4}
                                        value={formik.values.description}
                                        error={Boolean(formik.touched.description && formik.errors.description)}
                                    />
                                </Grid>
                                {!isEditMode && (
                                    <Grid item xs={4}>
                                        <TextField
                                            fullWidth
                                            label="Company Slug"
                                            name="companySlug"
                                            onBlur={formik.handleBlur}
                                            onChange={formik.handleChange}
                                            value={formik.values.companySlug}
                                            error={Boolean(formik.touched.companySlug && formik.errors.companySlug)}
                                            helperText={formik.touched.companySlug && formik.errors.companySlug}
                                        />
                                    </Grid>
                                )}

                                {/* Client Setup Type Section */}

                                <Grid item xs={12} sx={styles.dividerWrapper}>
                                    <Divider item flexItem orientation="horizontal" sx={styles.divider} />
                                </Grid>
                                <Grid xs={12} item sx={styles.headerWrapper}>
                                    <Grid xs={8.27}>
                                        <Typography>Client Setup Type</Typography>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4}>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={<Checkbox checked={formik.values.ansrHQ} name="ansrHQ" disabled />}
                                            label={'ANSR HQ'}
                                        />
                                    </FormGroup>
                                </Grid>
                                <Grid item xs={4}>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formik.values.clientHQ}
                                                    name="clientHQ"
                                                    disabled={isEditMode && company?.isClientHqEnabled ? true : false}
                                                    onChange={formik.handleChange}
                                                />
                                            }
                                            label={'Client HQ'}
                                        />
                                    </FormGroup>
                                </Grid>
                                {/* HQ Address Section */}
                                <Grid item xs={12} sx={styles.dividerWrapper}>
                                    <Divider item flexItem orientation="horizontal" sx={styles.divider} />
                                </Grid>

                                <Grid xs={12} item sx={styles.headerWrapper}>
                                    <Grid xs={8.27}>
                                        <Typography>Address</Typography>
                                    </Grid>
                                </Grid>

                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        helperText={formik.touched.locationName && formik.errors.locationName}
                                        error={Boolean(formik.touched.locationName && formik.errors.locationName)}
                                        label="Location name (Optional)"
                                        onBlur={formik.handleBlur}
                                        name={`locationName`}
                                        value={formik.values.locationName}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                                <Grid item xs={8}></Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        helperText={formik.touched.addressLine1 && formik.errors.addressLine1}
                                        error={Boolean(formik.touched.addressLine1 && formik.errors.addressLine1)}
                                        label="Address Line 1"
                                        onBlur={formik.handleBlur}
                                        name={`addressLine1`}
                                        value={formik.values.addressLine1}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        helperText={formik.touched.addressLine2 && formik.errors.addressLine2}
                                        error={Boolean(formik.touched.addressLine2 && formik.errors.addressLine2)}
                                        label="Address Line 2 (Optional)"
                                        onBlur={formik.handleBlur}
                                        name={`addressLine2`}
                                        value={formik.values.addressLine2}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        helperText={formik.touched.addressLine3 && formik.errors.addressLine3}
                                        error={Boolean(formik.touched.addressLine3 && formik.errors.addressLine3)}
                                        label="Address Line 3 (Optional)"
                                        onBlur={formik.handleBlur}
                                        name={`addressLine3`}
                                        value={formik.values.addressLine3}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        helperText={formik.touched.city && formik.errors.city}
                                        error={Boolean(formik.touched.city && formik.errors.city)}
                                        label="City"
                                        onBlur={formik.handleBlur}
                                        name={`city`}
                                        value={formik.values.city}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        helperText={formik.touched.state && formik.errors.state}
                                        error={Boolean(formik.touched.state && formik.errors.state)}
                                        label="State"
                                        onBlur={formik.handleBlur}
                                        name={`state`}
                                        value={formik.values.state}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        helperText={formik.touched.country && formik.errors.country}
                                        error={Boolean(formik.touched.country && formik.errors.country)}
                                        label="Country"
                                        onBlur={formik.handleBlur}
                                        name={`country`}
                                        value={formik.values.country}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>

                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        helperText={formik.touched.postalCode && formik.errors.postalCode}
                                        error={Boolean(formik.touched.postalCode && formik.errors.postalCode)}
                                        label="Postal/Zip code"
                                        onBlur={formik.handleBlur}
                                        name={`postalCode`}
                                        value={formik.values.postalCode}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>

                                {!isEditMode && (
                                    <>
                                        <Grid item xs={12} sx={styles.dividerWrapper}>
                                            <Divider item flexItem orientation="horizontal" sx={styles.divider} />
                                        </Grid>
                                        <Grid xs={12} item sx={styles.headerWrapper}>
                                            <Grid xs={8.27}>
                                                <Typography>Admin Details</Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                fullWidth
                                                helperText={formik.touched.email && formik.errors.email}
                                                error={Boolean(formik.touched.email && formik.errors.email)}
                                                label="Email"
                                                onBlur={formik.handleBlur}
                                                name={`email`}
                                                value={formik.values.email}
                                                onChange={(e) => {
                                                    const lowercased = e.target.value.toLowerCase();
                                                    formik.setFieldValue('email', lowercased);
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                fullWidth
                                                helperText={formik.touched.firstName && formik.errors.firstName}
                                                error={Boolean(formik.touched.firstName && formik.errors.firstName)}
                                                label="First name"
                                                onBlur={formik.handleBlur}
                                                name={`firstName`}
                                                value={formik.values.firstName}
                                                onChange={formik.handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                fullWidth
                                                helperText={formik.touched.lastName && formik.errors.lastName}
                                                error={Boolean(formik.touched.lastName && formik.errors.lastName)}
                                                label="Last name"
                                                onBlur={formik.handleBlur}
                                                name={`lastName`}
                                                value={formik.values.lastName}
                                                onChange={formik.handleChange}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                fullWidth
                                                helperText={formik.touched.contactNo && formik.errors.contactNo}
                                                error={Boolean(formik.touched.contactNo && formik.errors.contactNo)}
                                                label="Contact No."
                                                onBlur={formik.handleBlur}
                                                name={`contactNo`}
                                                value={formik.values.contactNo}
                                                onChange={formik.handleChange}
                                            />
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={12}>
                                    <Stack direction="row" justifyContent="end">
                                        <Button
                                            sx={utilStyles.mr(2)}
                                            color="primary"
                                            size="large"
                                            variant="outlined"
                                            onClick={() => navigate('/company')}
                                        >
                                            Cancel
                                        </Button>
                                        <Button color="primary" size="large" type="submit" variant="contained">
                                            {isEditMode ? 'Update' : 'Create'}
                                        </Button>
                                    </Stack>
                                </Grid>
                                {formik.errors.submit && (
                                    <Grid item xs={12}>
                                        <FormHelperText error>{formik.errors.submit}</FormHelperText>
                                    </Grid>
                                )}
                            </Grid>
                        </div>
                    </form>
                </Grid>
            </Grid>
        </>
    );
};

export default CompanyTab;
